/**
 * 通用下载工具函数
 *
 * 这个文件包含不依赖 Canvas 的下载相关函数，
 * 可以被 roboneo-chat 包安全地打包使用
 */

import { saveImageToPhotosAlbum, saveVideoToPhotosAlbum } from '@meitu/hogger';
import { message } from '$/components/Message';
import { urlToBlob } from './upload';

/**
 * 清理文件名，移除非法字符
 */
export function sanitizeFilename(name: string = '') {
  return name
    .replace(/[\r\n]/g, '') // 去除换行符
    .replace(/[\\/:*?"<>|]/g, '_') // 替换非法字符
    .trim();
}

/**
 * 从 URL 路径中提取文件扩展名（不含 .），去掉 query/hash 及 CDN 处理参数干扰
 * 例如：
 *   https://example.com/path/file.docx?token=xxx → 'docx'
 *   https://cdn.com/path/file.png!watermark-image-cn-ws-0.07?k=xxx → 'png'
 */
export function getExtensionFromUrl(url: string): string | undefined {
  try {
    const { pathname } = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    let lastSeg = pathname.split('/').filter(Boolean).pop();
    if (!lastSeg) return undefined;
    // 去掉 CDN 图片处理参数（'!' 后的部分，如 !watermark-xxx）
    const bangIdx = lastSeg.indexOf('!');
    if (bangIdx > 0) {
      lastSeg = lastSeg.slice(0, bangIdx);
    }
    const dotIdx = lastSeg.lastIndexOf('.');
    if (dotIdx > 0) {
      const ext = lastSeg.slice(dotIdx + 1).toLowerCase();
      // 校验扩展名合法性（1-10 位字母数字）
      if (/^[a-z0-9]{1,10}$/.test(ext)) {
        return ext;
      }
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

/**
 * 确保文件名携带正确的扩展名。
 * 优先使用从原始 URL 中提取到的扩展名，如果文件名已有扩展名且与 URL 扩展名不一致则替换，
 * 如果文件名没有扩展名则追加。
 */
export function ensureFilenameExtension(filename: string, urlExt: string | undefined): string {
  if (!urlExt) return filename;

  const dotIdx = filename.lastIndexOf('.');
  if (dotIdx > 0) {
    const currentExt = filename.slice(dotIdx + 1).toLowerCase();
    // 扩展名已一致（兼容 jpg/jpeg 同义）
    if (
      currentExt === urlExt
      || (urlExt === 'jpg' && currentExt === 'jpeg')
      || (urlExt === 'jpeg' && currentExt === 'jpg')
    ) {
      return filename;
    }
    // 替换为 URL 中的真实扩展名
    return `${filename.slice(0, dotIdx)}.${urlExt}`;
  }
  // 无扩展名，追加
  return `${filename}.${urlExt}`;
}

/**
 * 从 Content-Disposition 响应头解析文件名（优先 RFC 5987 的 filename*）
 */
export function parseFilenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;
  const utf8Match = header.match(/filename\*=(?:UTF-8''|utf-8'')([^;\s]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/["']/g, '').trim());
    } catch {
      /* ignore */
    }
  }
  const quoted = header.match(/filename="((?:\\.|[^"\\])*)"/i);
  if (quoted?.[1]) {
    return quoted[1].replace(/\\"/g, '"');
  }
  const unquoted = header.match(/filename=([^;\s]+)/i);
  if (unquoted?.[1]) {
    return unquoted[1].replace(/^["']|["']$/g, '').trim();
  }
  return undefined;
}

/**
 * 下载文件
 * @param href 文件 URL（可以是 blob URL 或普通 URL）
 * @param name 文件名（可选）
 */
export const downLoad = (href: string, name?: string) => {
  const a = document.createElement('a');
  document.body.appendChild(a);
  if (name) {
    a.download = sanitizeFilename(name);
  }
  const attachmentParam = 'response-content-disposition=attachment';
  let linkHref = href;
  if (/^https?:\/\//i.test(href) && !href.includes(attachmentParam)) {
    linkHref = `${href}${href.includes('?') ? '&' : '?'}${attachmentParam}`;
  }
  a.href = linkHref;
  a.click();
  document.body.removeChild(a);
};

function guessFilenameFromUrl(url: string): string | undefined {
  try {
    const { pathname } = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    const seg = pathname.split('/').filter(Boolean).pop();
    if (seg && /\.[a-z0-9]{1,10}$/i.test(seg)) {
      return decodeURIComponent(seg);
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

function isSameOriginUrl(url: string): boolean {
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

function isVideoUrl(url: string): boolean {
  return /\.(mp4|mov|avi|mkv|webm|ogg)$/i.test(url.split('?')[0]);
}

/**
 * 是否为 App 内嵌 WebView：与业务页一致优先读 `in_app=true`；
 * 若端上未带 query，则用「美图系 UA + MTJs 已注入」兜底（见 `SubApp` 埋点逻辑）。
 */
function isInAppWebViewContext(): boolean {
  if (typeof window === 'undefined') return false;
  if (new URLSearchParams(window.location.search).get('in_app') === 'true') return true;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (!/com\.meitu|com\.starii\.xdesign/i.test(ua)) return false;
  const win = window as Window & { MTJs?: unknown; };
  return Boolean(win.MTJs);
}

function isImageMediaUrl(url: string): boolean {
  return /\.(jpe?g|png|gif|webp|bmp|heic|heif|avif)$/i.test(url.split('?')[0]);
}

interface DownloadFileFromUrlOptions {
  preferSuggestedFilename?: boolean;
}

/**
 * 是否适合走 Hogger 存相册（视频 / 常见图片）；其它类型仍走 fetch + a.download。
 */
function isSavableAlbumMediaUrl(url: string, suggestedFilename?: string): boolean {
  if (isVideoUrl(url) || isImageMediaUrl(url)) return true;
  if (suggestedFilename && /\.(jpe?g|png|gif|webp|bmp|heic|heif|avif|mp4|mov|avi|mkv|webm|ogg)$/i.test(suggestedFilename)) {
    return true;
  }
  return false;
}

/**
 * App 内通过 Hogger 将远程图片/视频写入系统相册，并弹出与历史逻辑一致的提示。
 * @returns 是否保存成功
 */
async function saveRemoteMediaToPhotosAlbumWithMessages(url: string): Promise<boolean> {
  const saveToAlbum = isVideoUrl(url) ? saveVideoToPhotosAlbum : saveImageToPhotosAlbum;
  try {
    const [res] = await saveToAlbum(url);
    if (res) {
      message.success('roboneo_web_saved_successfully');
      return true;
    }
    message.error('roboneo_web_saved_failed');
    return false;
  } catch {
    message.error('roboneo_web_saved_failed');
    return false;
  }
}

/** 隐藏 iframe 触发下载（跨域且无法 fetch 时的兜底，不新开可见标签页） */
function downloadViaHiddenIframe(url: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.display = 'none';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.src = url;
  document.body.appendChild(iframe);
  setTimeout(() => {
    try {
      document.body.removeChild(iframe);
    } catch {
      /* ignore */
    }
  }, 120_000);
}

/**
 * 在当前页触发文件下载（优先 fetch 为 blob + 临时链接触发，避免 window.open 闪新标签）
 * @param url 下载地址（绝对或相对当前页）
 * @param suggestedFilename 当响应头无文件名时的建议名
 * @param options 下载行为配置
 */
export async function downloadFileFromUrl(
  url: string,
  suggestedFilename?: string,
  options: DownloadFileFromUrlOptions = {},
): Promise<void> {
  // App 内 WebView 对 blob + `<a download>` 支持差，图片/视频与 downloadImageUrl 一致走 Hogger 存相册
  if (isInAppWebViewContext() && isSavableAlbumMediaUrl(url, suggestedFilename)) {
    const ok = await saveRemoteMediaToPhotosAlbumWithMessages(url);
    if (!ok) throw new Error('roboneo_web_saved_failed');
    return;
  }

  const fallbackName = suggestedFilename || guessFilenameFromUrl(url) || 'download';
  // 从原始 URL 提取真实扩展名
  const urlExt = getExtensionFromUrl(url);
  const credentialModes: RequestCredentials[] = ['omit', 'include'];

  const tryDownloadWithCredentials = async (credentials: RequestCredentials): Promise<boolean> => {
    try {
      const response = await fetch(url, { mode: 'cors', credentials });
      if (!response.ok) return false;
      const blob = await response.blob();
      const fromHeader = parseFilenameFromContentDisposition(response.headers.get('Content-Disposition'));
      const rawFilename = sanitizeFilename(
        options.preferSuggestedFilename && suggestedFilename ? suggestedFilename : (fromHeader || fallbackName),
      );
      // 根据 URL 中的真实扩展名修正文件名
      const filename = ensureFilenameExtension(rawFilename, urlExt);
      const blobUrl = URL.createObjectURL(blob);
      downLoad(blobUrl, filename);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      return true;
    } catch {
      /* 尝试下一种 credentials 或其它兜底 */
      return false;
    }
  };

  const tryDownloadCredentialMode = async (index: number): Promise<boolean> => {
    if (index >= credentialModes.length) return false;
    const ok = await tryDownloadWithCredentials(credentialModes[index]);
    return ok || tryDownloadCredentialMode(index + 1);
  };

  if (await tryDownloadCredentialMode(0)) return;

  if (isSameOriginUrl(url)) {
    downLoad(url, sanitizeFilename(fallbackName));
    return;
  }

  downloadViaHiddenIframe(url);
}

/**
 * 下载图片 URL（name 可含扩展名；与 URL 推断后缀一致时不重复拼接）
 */
export async function downloadImageUrl({ url, name }: { url: string; name?: string; }) {
  if (isInAppWebViewContext()) {
    await saveRemoteMediaToPhotosAlbumWithMessages(url);
    return;
  }

  const blob = await urlToBlob(url);
  // 兼容旧浏览器，不使用正则lookbehind
  let match = null;
  const reg = /\/convert_save\/([^?]+)\.(jpe?g|png|webp)/i;
  const result = url.match(reg);
  if (result && result[1]) {
    match = [result[1]];
  }
  if (!name) {
    // eslint-disable-next-line no-param-reassign
    name = match ? match[0] : `${isVideoUrl(url) ? 'video' : 'image'}`;
  }

  const ext = getExtensionFromUrl(url) || blob.type.split('/')[1] || 'png';
  const filename = ensureFilenameExtension(sanitizeFilename(name), ext);

  const blobUrl = URL.createObjectURL(blob);
  downLoad(blobUrl, filename);
  // 延迟释放以确保下载已触发
  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
}

/**
 * @file 远程 Markdown 文本拉取，供资产库添加、拖拽上传、mock 注入等与真实链路对齐。
 */

/**
 * 通过 HTTP 请求拉取远程 Markdown 文件的纯文本内容。
 *
 * @param url - Markdown 资源的完整 URL
 * @returns 文本内容；失败时返回空字符串并打印错误日志
 */
export async function fetchMarkdownContentFromUrl(url: string): Promise<string> {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.error('fetchMarkdownContentFromUrl failed', res.status, url);
      return '';
    }
    return await res.text();
  } catch (e) {
    console.error('fetchMarkdownContentFromUrl error', e);
    return '';
  }
}
